// Source of truth: pillup-api/thirdParty/lsq/constants.js (LEAD_STAGES, OPPORTUNITY_STAGES,
// OPPORTUNITY_SUB_STAGES, OPPORTUNITY_STATUSES). There is no endpoint that serves these
// option lists today, so they are copied here by hand — if the backend enum changes, this
// file drifts silently. A `GET lsq/admin/transfer/filter-options` endpoint would remove that
// risk; see docs/feature/lsq-lead-transfer-ui.md for the follow-up note.

export const OPPORTUNITY_TRANSFER_SCOPE = {
    LATEST: "LATEST",
    ALL: "ALL",
    NONE: "NONE",
};

export const LSQ_TRANSFER_STATUS = {
    QUEUED: "QUEUED",
    RUNNING: "RUNNING",
    COMPLETED: "COMPLETED",
    COMPLETED_WITH_ERRORS: "COMPLETED_WITH_ERRORS",
    FAILED: "FAILED",
};

export const TERMINAL_TRANSFER_STATUSES = [
    LSQ_TRANSFER_STATUS.COMPLETED,
    LSQ_TRANSFER_STATUS.COMPLETED_WITH_ERRORS,
    LSQ_TRANSFER_STATUS.FAILED,
];

export const OPPORTUNITY_SCOPE_OPTIONS = [
    {
        value: OPPORTUNITY_TRANSFER_SCOPE.LATEST,
        label: "Latest opportunity only",
        description: "Moves only the most recently created opportunity on each lead.",
    },
    {
        value: OPPORTUNITY_TRANSFER_SCOPE.ALL,
        label: "All opportunities",
        description: "Moves every matching opportunity on each lead.",
    },
    {
        value: OPPORTUNITY_TRANSFER_SCOPE.NONE,
        label: "Leads only",
        description: "Moves lead ownership only; opportunities stay with the current owner.",
    },
];

// ← LEAD_STAGES
export const PROSPECT_STAGE_OPTIONS = [
    "Invalid / Junk",
    "New Lead",
    "Not Eligible",
    "Converted",
];

// ← OPPORTUNITY_STAGES — this is what mx_Lead_Stage actually holds (writes verified in
// thirdParty/lsq/functions.js). "Others" is not in the backend enum but is a real value
// LSQ writes, so it is added here as an option even though selectTransferSet has no
// dedicated sub-stage list for it.
export const MAIN_STAGE_OPTIONS = [
    "New Lead",
    "New Opportunity",
    "Interested in future",
    "Interested",
    "Converted",
    "Not Eligible",
    "Not interested",
    "Reorder Interested",
    "Others",
];

// Opportunity Stage is mx_Custom_2. The backend matches this filter against mx_Custom_2
// because the advanced-search endpoint it reads opportunities from returns only a fixed
// subset of custom fields (mx_Custom_1/2/6/8) — mx_Custom_44 is not available there.
export const OPPORTUNITY_STAGE_OPTIONS = MAIN_STAGE_OPTIONS;

// Lead type (mx_leadType) — values of TYPE_* in pillup-api/classes/Lead/Constants.js.
export const LEAD_TYPE_OPTIONS = [
    "CALLBACK",
    "REORDER",
    "LEAD",
    "LEAD_UNKNOWN_USER",
    "SELF_CHECKOUT",
];

// ← OPPORTUNITY_STATUSES, plus "Won"/"Lost" which LeadSquared uses but the backend
// constants file does not enumerate.
export const OPPORTUNITY_STATUS_OPTIONS = ["Open", "Won", "Lost"];

// ← OPPORTUNITY_SUB_STAGES, keyed by main stage exactly as the backend keys it, so the
// cascading sub-stage multi-select in Card 3 is a direct lookup. Main stages with no entry
// here ("New Opportunity", "Reorder Interested", "Others") simply contribute nothing to the
// cascade.
export const SUB_STAGES_BY_MAIN_STAGE = {
    "New Lead": ["RNR", "Callback", "Moved to Other Stages", "Doctor Consultation Pending"],
    "Interested in future": [
        "RNR",
        "Callback",
        "Delivery SLA not met",
        "Partial Fulfilment",
        "Already Purchased Medicines",
        "Other",
    ],
    "Interested": [
        "Requested for the First Quotation",
        "Needs more time",
        "Payment Pending / Quotation Sent",
        "PRx Pending",
        "Request for Quote Modification",
        "Complete Quote Sent by Pharmacist",
        "Partially Quote Sent by Pharmacist",
        "Quote Verified by Pharmacist",
        "Request for Quote Verification",
    ],
    "Converted": [
        "Consent Pending",
        "Pharmacist Review Pending",
        "Consent Received / Sent For Packaging",
        "Packed / Place Delivery",
        "Dispatched",
        "Delivered / Won",
    ],
    "Not interested": [
        "RNR after 3 Attempts",
        "RNR after Interested",
        "Wants more discount",
        "Wants Single Medicine",
        "Just for Information",
        "No Need",
        "Lost to a competitor",
        "Partial Fulfilment",
        "Rx not Readable",
        "Not Ready to share the prescription",
        "Other",
    ],
    "Not Eligible": [
        "Invalid Number",
        "Single Medicine",
        "H1 Drug",
        "Invalid Prx",
        "Surgical Instruments",
        "Pincode non serviceable",
        "Generic Medicine Required",
        "Monopoly Medicines",
        "Medicine Not Available",
        "Did not Clicked",
        "Other",
    ],
};

export const ALL_SUB_STAGE_OPTIONS = Array.from(
    new Set(Object.values(SUB_STAGES_BY_MAIN_STAGE).flat())
);

// Union of the sub-stages under the given main stages; falls back to every known sub-stage
// when no main stage is selected yet (per §4 of the spec).
export const getSubStageOptions = (selectedMainStages = []) => {
    if (!selectedMainStages.length) return ALL_SUB_STAGE_OPTIONS;

    const union = new Set();
    selectedMainStages.forEach((stage) => {
        (SUB_STAGES_BY_MAIN_STAGE[stage] || []).forEach((sub) => union.add(sub));
    });
    return Array.from(union);
};
